<template>
  <q-page padding>
    <div class="column q-gutter-y-md">
      <!-- Only render content when crud manager is ready -->
      <template v-if="crudManagerReady">
        <!-- Warehouse Selection -->
        <q-card>
          <q-card-section>
            <div class="row q-col-gutter-md items-center">
              <div class="col-12 col-sm-5">
                <CrudSelect
                  v-model="sourceWarehouseId"
                  model-name="warehouse"
                  :label="t('composites.StockManager.sourceWarehouseLabel')" />
              </div>
              <div class="col-12 col-sm-2 text-center">
                <q-icon
                  :name="symRoundedCompareArrows"
                  size="md"
                  color="grey-7" />
              </div>
              <div class="col-12 col-sm-5">
                <CrudSelect
                  v-model="destinationWarehouseId"
                  model-name="warehouse"
                  :label="t('composites.StockManager.destinationWarehouseLabel')" />
              </div>
            </div>
          </q-card-section>
        </q-card>

        <!-- Transfer Interface -->
        <template v-if="sourceWarehouseId && destinationWarehouseId && sourceWarehouseId !== destinationWarehouseId">
          <q-card>
            <q-card-section class="row items-center justify-between">
              <div class="text-h6">{{ t('composites.StockManager.transferItemsTitle') }}</div>
              <q-checkbox
                v-model="bypassQuantityLimits"
                :label="t('composites.StockManager.bypassQuantityLimitsLabel')">
                <q-tooltip>
                  {{ t('composites.StockManager.bypassQuantityLimitsTooltip') }}
                </q-tooltip>
              </q-checkbox>
            </q-card-section>

            <q-separator />

            <q-card-section>
              <crud-select
                v-model="selectedPartId"
                model-name="part"
                :label="t('composites.StockManager.addPartButtonLabel')"
                emit-value
                map-options
                outlined
                dense
                clearable
                :disable="loading"
                class="q-mb-md"
                @update:model-value="addPartToTransferList" />

              <q-markup-table
                v-if="transferItems.length"
                flat
                bordered
                wrap-cells>
                <thead>
                  <tr>
                    <th class="text-left">{{ t('composites.StockManager.tableHeaderPart') }}</th>
                    <th class="text-center">{{ t('composites.StockManager.tableHeaderW1Qty') }}</th>
                    <th class="text-center">{{ t('composites.StockManager.tableHeaderW1ToW2Transfer') }}</th>
                    <th class="text-center">{{ t('composites.StockManager.tableHeaderW2Qty') }}</th>
                    <th class="text-center">{{ t('composites.StockManager.tableHeaderW2ToW1Transfer') }}</th>
                    <th class="text-right">{{ t('composites.StockManager.tableHeaderActions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in transferItems"
                    :key="item.item_id">
                    <td class="text-left">
                      {{ item.item_name }}
                      <div class="text-caption text-grey-7">{{ item.part_details['oem'] }}</div>
                      <div class="q-mt-sm">
                        <q-select
                          v-model="selectedOptionIdByItemId[item.item_id]"
                          :options="partOptionsByItemId[item.item_id] || []"
                          label="Part option (optional)"
                          dense
                          clearable
                          emit-value
                          map-options
                          style="min-width: 220px" />
                      </div>
                    </td>
                    <td class="text-center">
                      <q-badge
                        :label="item.source_warehouse_stock.quantity"
                        color="blue-grey-7" />
                    </td>
                    <td
                      class="text-center"
                      style="min-width: 150px">
                      <q-input
                        v-model.number="item.transfer_to_destination_quantity"
                        type="number"
                        dense
                        outlined
                        :min="0"
                        :max="!bypassQuantityLimits ? item.source_warehouse_stock.quantity : undefined"
                        :rules="[
                          (val) =>
                            bypassQuantityLimits ||
                            val === null ||
                            val <= item.source_warehouse_stock.quantity ||
                            t('composites.StockManager.ruleExceedsW1Available'),
                          (val) => val === null || val >= 0 || t('composites.StockManager.ruleMustBeNonNegative'),
                        ]"
                        hide-bottom-space />
                    </td>
                    <td class="text-center">
                      <q-badge
                        :label="item.destination_warehouse_stock.quantity"
                        color="blue-grey-7" />
                    </td>
                    <td
                      class="text-center"
                      style="min-width: 150px">
                      <q-input
                        v-model.number="item.transfer_to_source_quantity"
                        type="number"
                        dense
                        outlined
                        :min="0"
                        :max="!bypassQuantityLimits ? item.destination_warehouse_stock.quantity : undefined"
                        :rules="[
                          (val) =>
                            bypassQuantityLimits ||
                            val === null ||
                            val <= item.destination_warehouse_stock.quantity ||
                            t('composites.StockManager.ruleExceedsW2Available'),
                          (val) => val === null || val >= 0 || t('composites.StockManager.ruleMustBeNonNegative'),
                        ]"
                        hide-bottom-space />
                    </td>
                    <td class="text-right">
                      <q-btn
                        flat
                        round
                        color="negative"
                        :icon="symRoundedDelete"
                        size="sm"
                        @click="removePartFromTransferList(index)" />
                    </td>
                  </tr>
                </tbody>
              </q-markup-table>
              <div
                v-else
                class="text-center text-grey-6 q-pa-md">
                {{ t('composites.StockManager.noItemsAdded') }}
              </div>
            </q-card-section>

            <q-separator v-if="transferItems.length" />
            <q-card-actions
              v-if="transferItems.length"
              align="right"
              class="q-pa-md">
              <q-btn
                :label="t('composites.StockManager.processButtonLabel')"
                color="primary"
                :icon="symRoundedSend"
                :loading="loading"
                :disable="!isTransferValid || !hasPendingTransfers"
                @click="handleProcessTransfers" />
            </q-card-actions>
          </q-card>
        </template>
      </template>

      <!-- Loading state while waiting for crud manager -->
      <template v-else>
        <q-card>
          <q-card-section class="text-center">
            <q-spinner
              color="primary"
              size="3em" />
            <div class="q-mt-md text-grey-7">{{ t('composites.StockManager.initializingLabel') }}</div>
          </q-card-section>
        </q-card>
      </template>

      <q-inner-loading
        :showing="loading"
        :label="t('composites.StockManager.loadingLabel')" />
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { symRoundedCompareArrows, symRoundedDelete, symRoundedSend } from '@quasar/extras/material-symbols-rounded'
import { useQuasar } from 'quasar'
import { showErrorNotification, showInfoNotification, showSuccessNotification, showWarningNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import CrudSelect from 'src/CrudManager/Core/composites/Form/CrudSelect.vue'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStockTransfer, type PairedStockItem, type StockTransferPayload } from '../composables/useStockTransfer'

const { t } = useI18n()
const $q = useQuasar()
const crudManagerStore = useCrudManagerStore()
const { loading, error, getPairedStock, processTransfers } = useStockTransfer()

// Wait for crud manager to be ready
const crudManagerReady = ref(false)

const sourceWarehouseId = ref<string | null>(null)
const destinationWarehouseId = ref<string | null>(null)
const bypassQuantityLimits = ref(false)
const selectedPartId = ref<string | null>(null) // For the CrudSelect to add parts
const isAddingPartId = ref<string | null>(null) // Guard for concurrent additions

const transferItems = ref<PairedStockItem[]>([])
const partOptionsByItemId = ref<Record<string, { label: string; value: string }[]>>({})
const selectedOptionIdByItemId = ref<Record<string, string | null>>({})

const onWarehouseSelectionChange = () => {
  transferItems.value = [] // Reset items if warehouse changes
  // Potentially auto-fetch if there are items already selected, or let user re-add
}

watch([sourceWarehouseId, destinationWarehouseId], () => {
  // If both warehouses are unselected, clear items
  if (!sourceWarehouseId.value && !destinationWarehouseId.value) {
    transferItems.value = []
  }
  // If one is unselected but not the other, also clear
  else if (!sourceWarehouseId.value || !destinationWarehouseId.value) {
    transferItems.value = []
  }
  // If both are selected and are the same, show notification and clear items
  else if (sourceWarehouseId.value === destinationWarehouseId.value) {
    showErrorNotification(t('composites.StockManager.notifyWarehousesMustBeDifferent'))
    transferItems.value = [] // Clear items as the selection is invalid for fetching/transferring
  }
})

async function addPartToTransferList(partId: string | null) {
  if (sourceWarehouseId.value && sourceWarehouseId.value === destinationWarehouseId.value) {
    showErrorNotification(t('composites.StockManager.notifyWarehousesMustBeDifferent'))
    selectedPartId.value = null // Reset chooser
    return
  }

  if (!partId || !sourceWarehouseId.value || !destinationWarehouseId.value) {
    selectedPartId.value = null // Reset chooser
    return
  }

  // Prevent adding if already fetching for this specific partId
  if (isAddingPartId.value === partId) {
    return
  }

  // Prevent adding duplicates that are already in the list
  if (transferItems.value.some((item) => item.item_id === partId)) {
    showWarningNotification(t('composites.StockManager.notifyAlreadyInList'))
    selectedPartId.value = null // Reset chooser
    return
  }

  try {
    isAddingPartId.value = partId // Set guard before async operation
    loading.value = true
    const newItemsData = await getPairedStock(sourceWarehouseId.value, destinationWarehouseId.value, [partId])
    if (newItemsData.length > 0) {
      transferItems.value.push(...newItemsData)
      // Load part options list lazily for the added part
      try {
        const { data } = await axiosApi.get(`/parts/${partId}/options`)
        const options = Array.isArray(data?.data) ? data.data : data
        partOptionsByItemId.value[partId] = (options || []).map((opt: any) => ({
          label: `${opt.name} ${opt.price ?? ''} ${opt.currency ?? ''}`,
          value: String(opt.id),
        }))
      } catch (err) {
        // silently ignore; options are optional
        partOptionsByItemId.value[partId] = []
      }
    } else {
      showErrorNotification(t('composites.StockManager.notifyCouldNotFetchPart', { partId }))
    }
  } catch (err) {
    console.error('Failed to add part to transfer list:', err)
    showErrorNotification(error.value || t('composites.StockManager.notifyFailedToFetchPartGeneric'), err)
  } finally {
    loading.value = false
    selectedPartId.value = null // Reset chooser after attempt
    isAddingPartId.value = null // Clear guard
  }
}

function removePartFromTransferList(index: number) {
  transferItems.value.splice(index, 1)
}

const isTransferValid = computed(() => {
  return transferItems.value.every((item) => {
    const toDestQty = item.transfer_to_destination_quantity
    const toSourceQty = item.transfer_to_source_quantity

    const validToDest =
      toDestQty === null || toDestQty === undefined || (toDestQty >= 0 && (bypassQuantityLimits.value || toDestQty <= item.source_warehouse_stock.quantity))
    const validToSource =
      toSourceQty === null ||
      toSourceQty === undefined ||
      (toSourceQty >= 0 && (bypassQuantityLimits.value || toSourceQty <= item.destination_warehouse_stock.quantity))

    return validToDest && validToSource
  })
})

const hasPendingTransfers = computed(() => {
  return transferItems.value.some(
    (item) =>
      (item.transfer_to_destination_quantity !== null && item.transfer_to_destination_quantity > 0) ||
      (item.transfer_to_source_quantity !== null && item.transfer_to_source_quantity > 0),
  )
})

async function handleProcessTransfers() {
  if (sourceWarehouseId.value && sourceWarehouseId.value === destinationWarehouseId.value) {
    showErrorNotification(t('composites.StockManager.notifyWarehousesMustBeDifferent'))
    return
  }

  if (!sourceWarehouseId.value || !destinationWarehouseId.value || !isTransferValid.value || !hasPendingTransfers.value) {
    showErrorNotification(t('composites.StockManager.notifyInvalidTransferQuantities'))
    return
  }

  const transfersToProcess: StockTransferPayload[] = []

  transferItems.value.forEach((item) => {
    // Transfer from Source (W1) to Destination (W2)
    if (item.transfer_to_destination_quantity !== null && item.transfer_to_destination_quantity > 0) {
      transfersToProcess.push({
        item_id: item.item_id,
        quantity: item.transfer_to_destination_quantity,
        source_warehouse_id: sourceWarehouseId.value!,
        destination_warehouse_id: destinationWarehouseId.value!,
        part_option_id: selectedOptionIdByItemId.value[item.item_id] || null,
      })
    }

    // Transfer from Destination (W2) to Source (W1)
    if (item.transfer_to_source_quantity !== null && item.transfer_to_source_quantity > 0) {
      transfersToProcess.push({
        item_id: item.item_id,
        quantity: item.transfer_to_source_quantity,
        source_warehouse_id: destinationWarehouseId.value!,
        destination_warehouse_id: sourceWarehouseId.value!,
        part_option_id: selectedOptionIdByItemId.value[item.item_id] || null,
      })
    }
  })

  if (transfersToProcess.length === 0) {
    showInfoNotification(t('composites.StockManager.notifyNoQuantitiesForTransfer'))
    return
  }

  try {
    await processTransfers(transfersToProcess, bypassQuantityLimits.value)
    showSuccessNotification(t('composites.StockManager.notifyTransferSuccess'))

    // Refresh stock data for the items that were part of the transfer
    const itemIdsInTransfer = transferItems.value.map((i) => i.item_id)
    transferItems.value = [] // Clear current list before fetching new data
    if (itemIdsInTransfer.length > 0 && sourceWarehouseId.value && destinationWarehouseId.value) {
      const updatedItemsData = await getPairedStock(sourceWarehouseId.value, destinationWarehouseId.value, itemIdsInTransfer)
      transferItems.value = updatedItemsData
    }
  } catch (err) {
    showErrorNotification(error.value || t('composites.StockManager.notifyTransferFailed'), err)
  }
}

onMounted(async () => {
  // Wait for crud manager to be initialized
  if (crudManagerStore.isInitiated) {
    // Store is already initiated
    crudManagerReady.value = true
  } else {
    // Store is not initiated, subscribe to wait for it
    const unsubscribe = crudManagerStore.$subscribe((mutation, state) => {
      if (state.isInitiated) {
        crudManagerReady.value = true
        unsubscribe() // Unsubscribe once ready
      }
    })
  }
})
</script>

<style lang="scss" scoped>
.q-markup-table th,
.q-markup-table td {
  padding: 8px 12px; // Adjust padding for tighter table
}

.q-table th {
  font-weight: bold;
}

.q-page {
  max-width: 1200px;
  margin: 0 auto;
}
</style>
